import { GoogleGenerativeAI } from '@google/generative-ai';
import { Chess } from 'chess.js';
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, increment, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase';

// Initialize Gemini AI
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

// OpenRouter API Key for GPT-oss (from environment variable)
const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY;

export const checkDailyLimit = async () => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('User not authenticated');

  const today = new Date().toISOString().split('T')[0];
  const limitDoc = await getDoc(doc(db, 'dailyLimits', userId));

  if (!limitDoc.exists()) {
    await setDoc(doc(db, 'dailyLimits', userId), {
      userId,
      date: today,
      matchesPlayed: 0,
      lastUpdated: serverTimestamp()
    });
    return { canPlay: true, matchesLeft: 3 };
  }

  const limitData = limitDoc.data();
  
  if (limitData.date !== today) {
    await updateDoc(doc(db, 'dailyLimits', userId), {
      date: today,
      matchesPlayed: 0,
      lastUpdated: serverTimestamp()
    });
    return { canPlay: true, matchesLeft: 3 };
  }

  const dailyLimit = 3;
  const matchesLeft = dailyLimit - limitData.matchesPlayed;
  
  return {
    canPlay: matchesLeft > 0,
    matchesLeft: Math.max(0, matchesLeft)
  };
};

export const getGeminiMove = async (fen, lastMove) => {
  const chess = new Chess();
  
  try {
    chess.load(fen);
  } catch (error) {
    throw new Error('Invalid board position');
  }

  // Add realistic thinking delay (2-4 seconds)
  const thinkingTime = 2000 + Math.random() * 2000;
  await new Promise(resolve => setTimeout(resolve, thinkingTime));

  // Get list of all legal moves for fallback
  const legalMoves = chess.moves({ verbose: true });
  
  // Get all captures
  const captures = legalMoves.filter(m => m.flags.includes('c') || m.flags.includes('e'));
  const capturesList = captures.length > 0 ? captures.map(m => m.san).join(', ') : 'none';
  
  // Analyze position
  const pieces = chess.board().flat().filter(p => p !== null);
  const moveNumber = Math.floor(chess.moveNumber());
  const gamePhase = moveNumber < 10 ? 'opening' : moveNumber < 40 ? 'middlegame' : 'endgame';
  
  // Get material count
  const material = {
    white: 0,
    black: 0
  };
  pieces.forEach(p => {
    const value = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 }[p.type];
    material[p.color === 'w' ? 'white' : 'black'] += value;
  });

  // Find which of my pieces are under attack
  const myColor = chess.turn(); // 'b' for black (MasterGemi)
  const attackedPieces = [];
  
  // Check each square for my pieces that are attacked
  const board = chess.board();
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === myColor) {
        const square = String.fromCharCode(97 + col) + (8 - row);
        if (chess.isAttacked(square, myColor === 'w' ? 'b' : 'w')) {
          const pieceValue = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 }[piece.type];
          attackedPieces.push({ square, piece: piece.type.toUpperCase(), value: pieceValue });
        }
      }
    }
  }
  
  const attackedList = attackedPieces.length > 0 
    ? attackedPieces.map(p => `${p.piece} on ${p.square} (${p.value}pts)`).join(', ')
    : 'none';

  // Find checks available
  const checksAvailable = legalMoves.filter(m => {
    const testChess = new Chess(fen);
    testChess.move(m.san);
    return testChess.inCheck();
  });
  const checksList = checksAvailable.length > 0 ? checksAvailable.map(m => m.san).join(', ') : 'none';

  // Find undefended enemy pieces
  const enemyColor = myColor === 'w' ? 'b' : 'w';
  const hangingPieces = [];
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === enemyColor && piece.type !== 'k') {
        const square = String.fromCharCode(97 + col) + (8 - row);
        // Check if we can capture it
        const canCapture = captures.some(c => c.to === square);
        if (canCapture) {
          const pieceValue = { p: 1, n: 3, b: 3, r: 5, q: 9 }[piece.type];
          hangingPieces.push({ square, piece: piece.type.toUpperCase(), value: pieceValue });
        }
      }
    }
  }
  const hangingList = hangingPieces.length > 0 
    ? hangingPieces.map(p => `${p.piece} on ${p.square} (${p.value}pts)`).join(', ')
    : 'none';

  const prompt = `You are MasterGemi, playing like MAGNUS CARLSEN - the greatest chess player in history. You are RUTHLESS, PRECISE, and NEVER make mistakes.

═══════════════════════════════════════════════════════════════
                    ⚠️ CRITICAL THREATS ⚠️
═══════════════════════════════════════════════════════════════
🛡️ YOUR PIECES UNDER ATTACK: ${attackedList}
${attackedPieces.length > 0 ? `
   ⚡ PRIORITY 1: You MUST address these threats FIRST!
   Options: Move piece to safety | Block attack | Capture attacker | Counter-attack bigger
` : '   ✅ Your pieces are safe'}

🎯 ENEMY PIECES YOU CAN CAPTURE: ${hangingList}
${hangingPieces.length > 0 ? `
   ⚡ FREE MATERIAL! Take it unless it loses you more!
` : ''}

👑 CHECKS AVAILABLE: ${checksList}
${checksAvailable.length > 0 ? `
   ⚡ Consider checking - it forces response!
` : ''}

═══════════════════════════════════════════════════════════════
                    📊 POSITION ANALYSIS
═══════════════════════════════════════════════════════════════
FEN: ${fen}
Last move: ${lastMove}
Phase: ${gamePhase.toUpperCase()} (Move ${moveNumber})
Material: White ${material.white} pts | Black ${material.black} pts
${material.white > material.black ? `⚠️ You are DOWN ${material.white - material.black} points - play carefully!` : 
  material.black > material.white ? `✅ You are UP ${material.black - material.white} points - simplify and win!` : 
  '⚖️ Material is equal'}

═══════════════════════════════════════════════════════════════
                    🎮 LEGAL MOVES
═══════════════════════════════════════════════════════════════
${legalMoves.map(m => m.san).join(', ')}

═══════════════════════════════════════════════════════════════
              🏆 MAGNUS CARLSEN DECISION TREE 🏆
═══════════════════════════════════════════════════════════════

STEP 1️⃣ SAFETY CHECK (DO THIS FIRST!)
${attackedPieces.length > 0 ? `
⚠️ YOUR ${attackedPieces.map(p => p.piece).join(', ')} ${attackedPieces.length === 1 ? 'IS' : 'ARE'} ATTACKED!
→ Can I move ${attackedPieces.length === 1 ? 'it' : 'the most valuable one'} to safety?
→ Can I capture the attacker?
→ Can I block the attack?
→ Can I counter-attack something MORE valuable?
⛔ DO NOT proceed to step 2 until threat is addressed!
` : '✅ No threats - proceed to Step 2'}

STEP 2️⃣ WINNING MATERIAL
${hangingPieces.length > 0 ? `
🎯 FREE PIECES: ${hangingList}
→ Capture the highest value piece!
→ Make sure YOUR capturing piece is safe after!
` : captures.length > 0 ? `
Captures available: ${capturesList}
→ Is any capture profitable? (their piece value > mine if recaptured)
→ Take if you WIN material!
` : '→ No profitable captures available'}

STEP 3️⃣ CREATING THREATS
${checksAvailable.length > 0 ? `
👑 You can give CHECK with: ${checksList}
→ Check is powerful - forces opponent to respond!
→ Does the check also attack another piece? (DOUBLE ATTACK!)
` : '→ No checks available'}
→ Can I attack their Queen?
→ Can I fork two pieces?
→ Can I pin a piece?
→ Can I skewer?

STEP 4️⃣ POSITIONAL PLAY (Magnus Carlsen's specialty!)
OPENING (moves 1-10):
→ Develop knights before bishops
→ Control center (e4, d4, e5, d5)
→ Castle early
→ Connect your rooks

MIDDLEGAME (moves 11-40):
→ Improve your worst piece
→ Create pawn weaknesses in enemy camp
→ Control open files with rooks
→ Put knights on outposts (protected by pawns)
→ Keep bishops on long diagonals

ENDGAME (move 41+):
→ Activate your king (centralize it!)
→ Create passed pawns
→ Rooks belong behind passed pawns
→ Calculate precisely - every tempo counts!

═══════════════════════════════════════════════════════════════
                    ⛔ MAGNUS NEVER DOES THIS
═══════════════════════════════════════════════════════════════
❌ NEVER ignore attacked pieces - that's amateur
❌ NEVER miss free captures - that's negligent  
❌ NEVER trade more valuable piece for less valuable
❌ NEVER make aimless moves - every move has purpose
❌ NEVER weaken king safety without compensation

═══════════════════════════════════════════════════════════════
                    ✅ MAGNUS ALWAYS DOES THIS
═══════════════════════════════════════════════════════════════
✅ FIRST protect threatened pieces
✅ THEN take free material
✅ THEN create threats
✅ THEN improve position
✅ Every piece must be defended or safe
✅ Think: "If I play this, what can opponent do?"

═══════════════════════════════════════════════════════════════

Now think like Magnus: calm, precise, deadly. What is the BEST move?

Respond with ONLY this JSON (no markdown, no explanation outside JSON):
{
  "move": "your move in SAN format",
  "commentary": "brief explanation (e.g., 'Saving my knight from attack' or 'Taking free bishop')"
}`;

  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
    const result = await model.generateContent(prompt);
    const responseText = result.response.text();
    
    console.log('Gemini raw response:', responseText); // Debug log
    
    let cleanedText = responseText.trim();
    cleanedText = cleanedText.replace(/```json\n?/g, '');
    cleanedText = cleanedText.replace(/\n?```/g, '');
    cleanedText = cleanedText.trim();
    
    console.log('Gemini cleaned response:', cleanedText); // Debug log
    
    const geminiResponse = JSON.parse(cleanedText);
    console.log('Gemini parsed response:', geminiResponse); // Debug log
    
    const move = chess.move(geminiResponse.move, { sloppy: true });
    
    if (!move) {
      console.warn('Gemini returned invalid move:', geminiResponse.move);
      // Smart fallback: prioritize captures, then checks, then any move
      const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
      
      return {
        move: smartMove,
        commentary: "Recalculating the best continuation...",
        wasInvalid: true
      };
    }
    
    console.log('Gemini returning commentary:', geminiResponse.commentary); // Debug log
    
    return {
      move: geminiResponse.move,
      commentary: geminiResponse.commentary || "Interesting position...",
      wasInvalid: false
    };
    
  } catch (error) {
    console.error('Gemini API Error:', error);
    console.error('Error details:', error.message, error.stack); // More detailed error
    // Smart fallback: prioritize captures, then checks, then any move
    const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
    
    return {
      move: smartMove,
      commentary: "An interesting position requires careful thought...",
      wasInvalid: true
    };
  }
};

// Smart fallback move selector - mimics Magnus Carlsen's priorities
const getSmartFallbackMove = (chess, legalMoves, captures, checksAvailable) => {
  const pieceValues = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
  
  // Priority 1: Take highest value capture
  if (captures.length > 0) {
    const sortedCaptures = captures.sort((a, b) => {
      const aValue = pieceValues[a.captured] || 0;
      const bValue = pieceValues[b.captured] || 0;
      return bValue - aValue;
    });
    return sortedCaptures[0].san;
  }
  
  // Priority 2: Give check if available
  if (checksAvailable.length > 0) {
    return checksAvailable[0].san;
  }
  
  // Priority 3: Prioritize developing moves in opening
  const developingMoves = legalMoves.filter(m => {
    // Castling is always good
    if (m.san === 'O-O' || m.san === 'O-O-O') return true;
    // Knight and bishop moves in opening
    if ((m.piece === 'n' || m.piece === 'b') && (m.from[1] === '1' || m.from[1] === '8')) return true;
    // Central pawn moves
    if (m.piece === 'p' && (m.to === 'e4' || m.to === 'd4' || m.to === 'e5' || m.to === 'd5')) return true;
    return false;
  });
  
  if (developingMoves.length > 0) {
    return developingMoves[Math.floor(Math.random() * developingMoves.length)].san;
  }
  
  // Priority 4: Random legal move
  return legalMoves[Math.floor(Math.random() * legalMoves.length)].san;
};

// GPT-oss (ChampGPT) move generator using OpenRouter API
export const getGPTossMove = async (fen, lastMove) => {
  const chess = new Chess();
  
  try {
    chess.load(fen);
  } catch (error) {
    throw new Error('Invalid board position');
  }

  // Add realistic thinking delay (2-4 seconds)
  const thinkingTime = 2000 + Math.random() * 2000;
  await new Promise(resolve => setTimeout(resolve, thinkingTime));

  // Get list of all legal moves for fallback
  const legalMoves = chess.moves({ verbose: true });
  
  // Get all captures
  const captures = legalMoves.filter(m => m.flags.includes('c') || m.flags.includes('e'));
  const capturesList = captures.length > 0 ? captures.map(m => m.san).join(', ') : 'none';
  
  // Analyze position
  const pieces = chess.board().flat().filter(p => p !== null);
  const moveNumber = Math.floor(chess.moveNumber());
  const gamePhase = moveNumber < 10 ? 'opening' : moveNumber < 40 ? 'middlegame' : 'endgame';
  
  // Get material count
  const material = { white: 0, black: 0 };
  pieces.forEach(p => {
    const value = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 }[p.type];
    material[p.color === 'w' ? 'white' : 'black'] += value;
  });

  // Find which of my pieces are under attack
  const myColor = chess.turn();
  const attackedPieces = [];
  const board = chess.board();
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === myColor) {
        const square = String.fromCharCode(97 + col) + (8 - row);
        if (chess.isAttacked(square, myColor === 'w' ? 'b' : 'w')) {
          const pieceValue = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 }[piece.type];
          attackedPieces.push({ square, piece: piece.type.toUpperCase(), value: pieceValue });
        }
      }
    }
  }
  
  const attackedList = attackedPieces.length > 0 
    ? attackedPieces.map(p => `${p.piece} on ${p.square} (${p.value}pts)`).join(', ')
    : 'none';

  // Find checks available
  const checksAvailable = legalMoves.filter(m => {
    const testChess = new Chess(fen);
    testChess.move(m.san);
    return testChess.inCheck();
  });

  const prompt = `You are ChampGPT, a world-class chess AI powered by GPT. You play like GARRY KASPAROV - aggressive, tactical, and brilliant.

POSITION: ${fen}
Last opponent move: ${lastMove}
Game phase: ${gamePhase} (move ${moveNumber})
Material: White ${material.white} pts | Black ${material.black} pts

YOUR PIECES UNDER ATTACK: ${attackedList}
${attackedPieces.length > 0 ? 'WARNING: Save your pieces first!' : ''}

CAPTURES AVAILABLE: ${capturesList}

ALL LEGAL MOVES: ${legalMoves.map(m => m.san).join(', ')}

PRIORITIES:
1. DEFEND attacked pieces (move them, block, or capture attacker)
2. CAPTURE free/undefended pieces
3. Give CHECK if safe
4. Create tactical threats (forks, pins, skewers)
5. Improve piece activity

NEVER:
- Ignore attacked pieces
- Miss free captures
- Trade bigger pieces for smaller ones
- Make purposeless moves

Respond with ONLY valid JSON (no markdown):
{"move": "your move in SAN notation", "commentary": "brief explanation"}`;

  try {
    // Check if API key exists
    if (!OPENROUTER_API_KEY) {
      console.error('OpenRouter API key not found. Please restart the dev server after adding REACT_APP_OPENROUTER_API_KEY to .env');
      throw new Error('OpenRouter API key not configured');
    }

    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENROUTER_API_KEY}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": "openai/gpt-4o-mini",
        "messages": [
          {
            "role": "system",
            "content": "You are ChampGPT, a chess grandmaster AI. You respond ONLY with valid JSON containing your move and commentary. No markdown, no extra text."
          },
          {
            "role": "user",
            "content": prompt
          }
        ],
        "temperature": 0.3,
        "max_tokens": 150
      })
    });

    const result = await response.json();
    
    console.log('OpenRouter API Response:', result); // Debug log
    
    if (!result.choices || !result.choices[0]) {
      console.error('Invalid API response structure:', result);
      throw new Error('Invalid API response');
    }

    let responseText = result.choices[0].message.content.trim();
    console.log('Raw response text:', responseText); // Debug log
    responseText = responseText.replace(/```json\n?/g, '');
    responseText = responseText.replace(/\n?```/g, '');
    responseText = responseText.trim();
    
    const gptResponse = JSON.parse(responseText);
    console.log('GPT Response:', gptResponse); // Debug log
    
    const move = chess.move(gptResponse.move, { sloppy: true });
    
    if (!move) {
      console.warn('GPT-oss returned invalid move:', gptResponse.move);
      const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
      
      return {
        move: smartMove,
        commentary: "Recalculating the position...",
        wasInvalid: true
      };
    }
    
    console.log('Returning commentary:', gptResponse.commentary); // Debug log
    
    return {
      move: gptResponse.move,
      commentary: gptResponse.commentary || "Analyzing the position...",
      wasInvalid: false
    };
    
  } catch (error) {
    console.error('GPT-oss API Error:', error);
    const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
    
    return {
      move: smartMove,
      commentary: "Analyzing this complex position...",
      wasInvalid: true
    };
  }
};

// NVIDIA Nemotron (NemoTron) move generator using OpenRouter API
export const getNvidiaMove = async (fen, lastMove) => {
  const chess = new Chess();
  
  try {
    chess.load(fen);
  } catch (error) {
    throw new Error('Invalid board position');
  }

  // Add realistic thinking delay (2-4 seconds)
  const thinkingTime = 2000 + Math.random() * 2000;
  await new Promise(resolve => setTimeout(resolve, thinkingTime));

  // Get list of all legal moves for fallback
  const legalMoves = chess.moves({ verbose: true });
  
  // Get all captures
  const captures = legalMoves.filter(m => m.flags.includes('c') || m.flags.includes('e'));
  const capturesList = captures.length > 0 ? captures.map(m => m.san).join(', ') : 'none';
  
  // Analyze position
  const pieces = chess.board().flat().filter(p => p !== null);
  const moveNumber = Math.floor(chess.moveNumber());
  const gamePhase = moveNumber < 10 ? 'opening' : moveNumber < 40 ? 'middlegame' : 'endgame';
  
  // Get material count
  const material = { white: 0, black: 0 };
  pieces.forEach(p => {
    const value = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 }[p.type];
    material[p.color === 'w' ? 'white' : 'black'] += value;
  });

  // Find which of my pieces are under attack
  const myColor = chess.turn();
  const attackedPieces = [];
  const board = chess.board();
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === myColor) {
        const square = String.fromCharCode(97 + col) + (8 - row);
        if (chess.isAttacked(square, myColor === 'w' ? 'b' : 'w')) {
          const pieceValue = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 }[piece.type];
          attackedPieces.push({ square, piece: piece.type.toUpperCase(), value: pieceValue });
        }
      }
    }
  }
  
  const attackedList = attackedPieces.length > 0 
    ? attackedPieces.map(p => `${p.piece} on ${p.square} (${p.value}pts)`).join(', ')
    : 'none';

  // Find checks available
  const checksAvailable = legalMoves.filter(m => {
    const testChess = new Chess(fen);
    testChess.move(m.san);
    return testChess.inCheck();
  });

  const prompt = `You are NemoTron, a powerful chess AI powered by NVIDIA. You play like BOBBY FISCHER - creative, aggressive, and brilliant at calculating tactics.

POSITION: ${fen}
Last opponent move: ${lastMove}
Game phase: ${gamePhase} (move ${moveNumber})
Material: White ${material.white} pts | Black ${material.black} pts

YOUR PIECES UNDER ATTACK: ${attackedList}
${attackedPieces.length > 0 ? 'WARNING: Save your pieces first!' : ''}

CAPTURES AVAILABLE: ${capturesList}

ALL LEGAL MOVES: ${legalMoves.map(m => m.san).join(', ')}

PRIORITIES:
1. DEFEND attacked pieces (move them, block, or capture attacker)
2. CAPTURE free/undefended pieces
3. Give CHECK if safe
4. Create tactical threats (forks, pins, skewers)
5. Improve piece activity

NEVER:
- Ignore attacked pieces
- Miss free captures
- Trade bigger pieces for smaller ones
- Make purposeless moves

Respond with ONLY valid JSON (no markdown):
{"move": "your move in SAN notation", "commentary": "brief explanation"}`;

  try {
    if (!OPENROUTER_API_KEY) {
      console.error('OpenRouter API key not found');
      throw new Error('OpenRouter API key not configured');
    }

    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENROUTER_API_KEY}`,
        "HTTP-Referer": "https://lmc-arena.com",
        "X-Title": "LMC Arena",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": "nvidia/nemotron-nano-9b-v2:free",
        "messages": [
          {
            "role": "system",
            "content": "You are NemoTron, a chess grandmaster AI powered by NVIDIA. You respond ONLY with valid JSON containing your move and commentary. No markdown, no extra text."
          },
          {
            "role": "user",
            "content": prompt
          }
        ],
        "temperature": 0.3,
        "max_tokens": 150
      })
    });

    const result = await response.json();
    
    console.log('NVIDIA API Response:', result);
    
    if (!result.choices || !result.choices[0]) {
      console.error('Invalid NVIDIA API response:', result);
      throw new Error('Invalid API response');
    }

    let responseText = result.choices[0].message.content.trim();
    console.log('NVIDIA raw response:', responseText);
    
    responseText = responseText.replace(/```json\n?/g, '');
    responseText = responseText.replace(/\n?```/g, '');
    responseText = responseText.trim();
    
    const nvidiaResponse = JSON.parse(responseText);
    console.log('NVIDIA parsed response:', nvidiaResponse);
    
    const move = chess.move(nvidiaResponse.move, { sloppy: true });
    
    if (!move) {
      console.warn('NVIDIA returned invalid move:', nvidiaResponse.move);
      const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
      
      return {
        move: smartMove,
        commentary: "Recalculating optimal strategy...",
        wasInvalid: true
      };
    }
    
    console.log('NVIDIA returning commentary:', nvidiaResponse.commentary);
    
    return {
      move: nvidiaResponse.move,
      commentary: nvidiaResponse.commentary || "Processing position...",
      wasInvalid: false
    };
    
  } catch (error) {
    console.error('NVIDIA API Error:', error);
    const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
    
    return {
      move: smartMove,
      commentary: "Computing optimal move...",
      wasInvalid: true
    };
  }
};

export const incrementMatchCount = async () => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('User not authenticated');

  await updateDoc(doc(db, 'dailyLimits', userId), {
    matchesPlayed: increment(1),
    lastUpdated: serverTimestamp()
  });
};

// Player AI Takeover - Uses Gemini to play on behalf of the user
export const getPlayerAIMove = async (fen, lastOpponentMove) => {
  const chess = new Chess();
  
  try {
    chess.load(fen);
  } catch (error) {
    throw new Error('Invalid board position');
  }

  // Add thinking delay (1-2 seconds for player AI)
  const thinkingTime = 1000 + Math.random() * 1000;
  await new Promise(resolve => setTimeout(resolve, thinkingTime));

  // Get list of all legal moves for fallback
  const legalMoves = chess.moves({ verbose: true });
  
  if (legalMoves.length === 0) {
    throw new Error('No legal moves available');
  }
  
  // Get all captures
  const captures = legalMoves.filter(m => m.flags.includes('c') || m.flags.includes('e'));
  const capturesList = captures.length > 0 ? captures.map(m => m.san).join(', ') : 'none';
  
  // Analyze position
  const pieces = chess.board().flat().filter(p => p !== null);
  const moveNumber = Math.floor(chess.moveNumber());
  const gamePhase = moveNumber < 10 ? 'opening' : moveNumber < 40 ? 'middlegame' : 'endgame';
  
  // Get material count
  const material = { white: 0, black: 0 };
  pieces.forEach(p => {
    const value = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 }[p.type];
    material[p.color === 'w' ? 'white' : 'black'] += value;
  });

  // Find which of my pieces are under attack
  const myColor = chess.turn(); // 'w' for white (player)
  const attackedPieces = [];
  const board = chess.board();
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === myColor) {
        const square = String.fromCharCode(97 + col) + (8 - row);
        if (chess.isAttacked(square, myColor === 'w' ? 'b' : 'w')) {
          const pieceValue = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 }[piece.type];
          attackedPieces.push({ square, piece: piece.type.toUpperCase(), value: pieceValue });
        }
      }
    }
  }
  
  const attackedList = attackedPieces.length > 0 
    ? attackedPieces.map(p => `${p.piece} on ${p.square} (${p.value}pts)`).join(', ')
    : 'none';

  // Find checks available
  const checksAvailable = legalMoves.filter(m => {
    const testChess = new Chess(fen);
    testChess.move(m.san);
    return testChess.inCheck();
  });

  // Find hanging enemy pieces
  const enemyColor = myColor === 'w' ? 'b' : 'w';
  const hangingPieces = [];
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === enemyColor && piece.type !== 'k') {
        const square = String.fromCharCode(97 + col) + (8 - row);
        const canCapture = captures.some(c => c.to === square);
        if (canCapture) {
          const pieceValue = { p: 1, n: 3, b: 3, r: 5, q: 9 }[piece.type];
          hangingPieces.push({ square, piece: piece.type.toUpperCase(), value: pieceValue });
        }
      }
    }
  }

  const prompt = `You are AutoPilot AI, playing as WHITE to help the player defeat the opponent AI. Play like a GRANDMASTER.

POSITION (FEN): ${fen}
Opponent's last move: ${lastOpponentMove || 'Game just started'}
Game phase: ${gamePhase} (move ${moveNumber})
Material: White ${material.white} pts | Black ${material.black} pts

YOUR PIECES UNDER ATTACK: ${attackedList}
CAPTURES AVAILABLE: ${capturesList}
CHECKS AVAILABLE: ${checksAvailable.map(m => m.san).join(', ') || 'none'}

ALL LEGAL MOVES: ${legalMoves.map(m => m.san).join(', ')}

STRATEGY:
1. SAVE pieces under attack first
2. CAPTURE free pieces
3. Give CHECK if beneficial
4. Create threats and improve position
5. Play principled chess - control center, develop pieces, castle early

Respond with ONLY valid JSON (no markdown):
{"move": "your move in SAN notation", "thinking": "brief explanation of your choice"}`;

  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
    const result = await model.generateContent(prompt);
    const responseText = result.response.text();
    
    console.log('Player AI raw response:', responseText);
    
    let cleanedText = responseText.trim();
    cleanedText = cleanedText.replace(/```json\n?/g, '');
    cleanedText = cleanedText.replace(/\n?```/g, '');
    cleanedText = cleanedText.trim();
    
    const aiResponse = JSON.parse(cleanedText);
    console.log('Player AI parsed response:', aiResponse);
    
    const move = chess.move(aiResponse.move, { sloppy: true });
    
    if (!move) {
      console.warn('Player AI returned invalid move:', aiResponse.move);
      const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
      
      return {
        move: smartMove,
        thinking: "Finding the best continuation...",
        wasInvalid: true
      };
    }
    
    return {
      move: aiResponse.move,
      thinking: aiResponse.thinking || "Calculating...",
      wasInvalid: false
    };
    
  } catch (error) {
    console.error('Player AI Error:', error);
    const smartMove = getSmartFallbackMove(chess, legalMoves, captures, checksAvailable);
    
    return {
      move: smartMove,
      thinking: "Analyzing position...",
      wasInvalid: true
    };
  }
};

export const saveMatchResult = async (result, moves, finalFen) => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('User not authenticated');

  if (!['win', 'loss', 'draw'].includes(result)) {
    throw new Error('Invalid result value');
  }

  await addDoc(collection(db, 'matches'), {
    userId,
    result,
    playedAt: serverTimestamp(),
    moves: moves || [],
    finalFen: finalFen || ''
  });

  const userRef = doc(db, 'users', userId);
  const updateData = {
    'stats.totalGames': increment(1)
  };

  if (result === 'win') {
    updateData['stats.wins'] = increment(1);
  } else if (result === 'loss') {
    updateData['stats.losses'] = increment(1);
  } else if (result === 'draw') {
    updateData['stats.draws'] = increment(1);
  }

  await updateDoc(userRef, updateData);

  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();

  return {
    success: true,
    stats: userData.stats
  };
};
